import { useCallback, useEffect, useRef, useState } from "react";
import { Connection, Edge, OnConnectStartParams } from "@xyflow/react";

export type SequenceCanvasMenuPosition = {
  x: number;
  y: number;
  screenX: number;
  screenY: number;
};

type UseSequenceCanvasConnectionLifecycleParams = {
  edges: Edge[];
  screenToFlowPosition: (position: { x: number; y: number }) => {
    x: number;
    y: number;
  };
};

export function useSequenceCanvasConnectionLifecycle({
  edges,
  screenToFlowPosition,
}: UseSequenceCanvasConnectionLifecycleParams) {
  const [menu, setMenu] = useState<SequenceCanvasMenuPosition | null>(null);
  const [pendingConnectionSource, setPendingConnectionSource] =
    useState<OnConnectStartParams | null>(null);
  const pendingConnectionSourceRef = useRef<OnConnectStartParams | null>(null);
  const [temporaryEdgeTarget, setTemporaryEdgeTarget] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const temporaryEdgeIdRef = useRef<string | null>(null);
  const [edgeToSplit, setEdgeToSplit] = useState<Edge | null>(null);

  useEffect(() => {
    pendingConnectionSourceRef.current = pendingConnectionSource;
  }, [pendingConnectionSource]);

  const clearPendingConnection = useCallback(() => {
    setPendingConnectionSource(null);
    pendingConnectionSourceRef.current = null;
    setTemporaryEdgeTarget(null);
    temporaryEdgeIdRef.current = null;
  }, []);

  const onPaneContextMenu = useCallback(
    (event: React.MouseEvent, bounds: DOMRect) => {
      event.preventDefault();
      setMenu({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
        screenX: event.clientX,
        screenY: event.clientY,
      });
    },
    []
  );

  const onCloseMenu = useCallback(() => {
    setMenu(null);
    clearPendingConnection();
    setEdgeToSplit(null);
  }, [clearPendingConnection]);

  const onConnectStart = useCallback(
    (_event: MouseEvent | TouchEvent, params: OnConnectStartParams) => {
      setPendingConnectionSource(params);
      pendingConnectionSourceRef.current = params;
    },
    []
  );

  const onConnectEnd = useCallback(
    (
      event: MouseEvent | TouchEvent,
      connectionState: any,
      reactFlowWrapper: HTMLDivElement | null
    ) => {
      const connection: Connection | null =
        connectionState?.connection ?? connectionState ?? null;
      const currentPendingConnection = pendingConnectionSourceRef.current;

      if (!currentPendingConnection) {
        return;
      }

      const hasCompleteConnection = connection?.source && connection?.target;
      if (hasCompleteConnection) {
        clearPendingConnection();
        return;
      }

      if (!reactFlowWrapper) {
        clearPendingConnection();
        return;
      }

      const bounds = reactFlowWrapper.getBoundingClientRect();
      const mouseEvent = event as MouseEvent;

      const menuPosition = {
        x: mouseEvent.clientX - bounds.left,
        y: mouseEvent.clientY - bounds.top,
        screenX: mouseEvent.clientX,
        screenY: mouseEvent.clientY,
      };

      const flowPosition = screenToFlowPosition({
        x: mouseEvent.clientX,
        y: mouseEvent.clientY,
      });

      if (!temporaryEdgeIdRef.current) {
        temporaryEdgeIdRef.current = `temp-connection-${Date.now()}`;
      }

      setMenu(menuPosition);
      setTemporaryEdgeTarget(flowPosition);
    },
    [clearPendingConnection, screenToFlowPosition]
  );

  const onEdgeClick = useCallback(
    (
      edgeId: string,
      relativeX: number,
      relativeY: number,
      screenX: number,
      screenY: number
    ) => {
      const clickedEdge = edges.find((edge) => edge.id === edgeId);
      if (!clickedEdge) return;

      setEdgeToSplit(clickedEdge);
      setMenu({
        x: relativeX,
        y: relativeY,
        screenX,
        screenY,
      });
    },
    [edges]
  );

  return {
    menu,
    setMenu,
    pendingConnectionSource,
    temporaryEdgeTarget,
    temporaryEdgeIdRef,
    edgeToSplit,
    clearPendingConnection,
    onPaneContextMenu,
    onCloseMenu,
    onConnectStart,
    onConnectEnd,
    onEdgeClick,
  };
}
